class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('.predictive-search__results');
    this.overlay = this.querySelector('.predictive-search__overlay');
    this.isOpen = false;
    this.controller = new AbortController();
    this.working = false;
    this.hasPlaceholder = false;
    this.timeoutQuery;

    // API fallback (used when native predictive search returns no products)
    this.apiCountry = this.getAttribute('data-api-country') || '';
    this.viewAllLabel = this.getAttribute('data-view-all-label') || '';
    this.cachedApiResults = {};
    this.apiController = new AbortController();

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', (event) => {
      this.open();
      this.setLiveRegionLoadingState();
      if(this.timeoutQuery) {
        clearTimeout(this.timeoutQuery);
        this.working = false;
      }
      this.timeoutQuery = setTimeout(this.onChange.bind(this), 300);
    });
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.overlay.addEventListener('click', this.onClickOverlay.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();
    if (!searchTerm.length) {
      this.close(true);
      return;
    }
    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onClickOverlay() {
    this.close();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    
    if (!Shopify.designMode && this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      this.runApiFallback(searchTerm, queryKey);
      return;
    }

    if(this.working) {
      this.controller.abort();
      this.controller = new AbortController();
    }
    const signal = this.controller.signal;
    this.working = true;

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[limit]')}=10&section_id=predictive-search`, { signal })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = getDomHtmlFromText(text, '#shopify-section-predictive-search');
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
        this.runApiFallback(searchTerm, queryKey);
      })
      .catch((error) => {
        this.close();
        throw error;
      })
      .finally(() => {
        this.working = false;
      });
  }

  setLiveRegionLoadingState() {
    this.setAttribute('loading', true);
    if(!this.hasPlaceholder) {
      const temp = document.getElementById("predictive-search-placeholder-tpl");
      const placeholder = temp.content.cloneNode(true);
      this.querySelector('.predictive-search__loading-state').append(placeholder);
      this.hasPlaceholder = true;
    }
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup.innerHTML;

    this.predictiveSearchResults.querySelectorAll('script').forEach(oldScriptTag => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach(attribute => {
        newScriptTag.setAttribute(attribute.name, attribute.value)
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });

    this.setAttribute('results', true);

    this.setLiveRegionResults();
  }

  // When native predictive search finds no products, ask the search API for
  // matches it may have missed, then render them using Shopify product data.
  runApiFallback(searchTerm, queryKey) {
    if (!this.apiCountry) return;
    if (this.predictiveSearchResults.querySelector('.predictive-search__product')) return;

    if (Object.prototype.hasOwnProperty.call(this.cachedApiResults, queryKey)) {
      this.injectApiResults(this.cachedApiResults[queryKey], searchTerm);
      return;
    }

    this.apiController.abort();
    this.apiController = new AbortController();
    const signal = this.apiController.signal;
    this.showApiLoading();

    fetch(`https://api-ott.manta.link/api/search?q=${encodeURIComponent(searchTerm)}&country=${encodeURIComponent(this.apiCountry)}&limit=6`, {
      signal,
      headers: { 'Accept': 'application/json' }
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        const ids = data && data.success && Array.isArray(data.results)
          ? data.results.map((item) => item.product_id).filter(Boolean)
          : [];
        if (!ids.length) {
          this.cachedApiResults[queryKey] = '';
          return null;
        }
        return this.fetchApiProducts(ids, queryKey);
      })
      .then((markup) => {
        this.injectApiResults(markup || '', searchTerm);
      })
      .catch(() => {
        // Abort or network errors fall back to the default behaviour silently.
        if (this.getQuery() === searchTerm) this.hideApiLoading(false);
      });
  }

  // Resolve API product IDs to rendered product markup through Shopify's search
  // (an `id:` query matches products even when the text query cannot).
  fetchApiProducts(ids, queryKey) {
    const idsQuery = ids.map((id) => `id:${id}`).join(' OR ');
    const url = `${routes.search_url}?section_id=predictive-search-api&q=${encodeURIComponent(idsQuery)}&type=product`;

    return fetch(url, { signal: this.apiController.signal })
      .then((response) => (response.ok ? response.text() : ''))
      .then((text) => {
        const container = getDomHtmlFromText(text, '.predictive-search__api-results');
        const markup = container && container.querySelector('.predictive-search__product') ? container.outerHTML : '';
        this.cachedApiResults[queryKey] = markup;
        return markup;
      });
  }

  injectApiResults(markup, searchTerm) {
    // Ignore stale responses: the query changed while the API was working.
    if (this.getQuery() !== searchTerm) return;

    const resultsList = this.predictiveSearchResults.querySelector('.predictive-search__results-list');
    if (!markup || !resultsList) {
      // Nothing found — keep the "search for X" link as the only fallback.
      this.hideApiLoading(false);
      return;
    }

    // Products found — drop the spinner and the now-redundant full-search link.
    this.hideApiLoading(true);
    resultsList.insertAdjacentHTML('beforeend', markup);
    this.appendViewAllButton(resultsList, searchTerm);
    this.setAttribute('results', true);
  }

  // Add the "view all results" button after API-injected products so it matches
  // the native results state (it is server-rendered only when native search hits).
  appendViewAllButton(resultsList, searchTerm) {
    if (!this.viewAllLabel) return;
    if (resultsList.nextElementSibling && resultsList.nextElementSibling.classList.contains('predictive-search__search-url')) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'center predictive-search__search-url element-small-margin-top break';

    const link = document.createElement('a');
    link.className = 'predictive-search__button';
    link.href = `${routes.search_url}?q=${encodeURIComponent(searchTerm)}&options%5Bprefix%5D=last`;

    const span = document.createElement('span');
    span.textContent = this.viewAllLabel;

    link.appendChild(span);
    wrapper.appendChild(link);
    resultsList.insertAdjacentElement('afterend', wrapper);
  }

  showApiLoading() {
    const resultsList = this.predictiveSearchResults.querySelector('.predictive-search__results-list');
    if (!resultsList) return;

    const fullLink = this.predictiveSearchResults.querySelector('.predictive-search__header');
    if (fullLink) fullLink.classList.add('hidden');

    if (this.predictiveSearchResults.querySelector('.predictive-search__api-loading')) return;
    const loader = document.createElement('div');
    loader.className = 'predictive-search__api-loading center element-small-margin-top';
    loader.innerHTML = '<div class="loading-overlay__spinner"><svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg"><circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg></div>';
    resultsList.appendChild(loader);
  }

  hideApiLoading(foundProducts) {
    const loader = this.predictiveSearchResults.querySelector('.predictive-search__api-loading');
    if (loader) loader.remove();

    const fullLink = this.predictiveSearchResults.querySelector('.predictive-search__header');
    if (!fullLink) return;
    if (foundProducts) {
      fullLink.remove();
    } else {
      fullLink.classList.remove('hidden');
    }
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
  }

  open() {
    if(!this.isOpen) {
      if(!this.classList.contains('run-top')) {
        if(!this.timeoutRunTop) {
          this.timeoutRunTop = setTimeout(() => {
            this.classList.add('run-top');
            this.timeoutRunTop = null;
          }, 500);
        }
      }
      
      this.setAttribute('open', true);
      this.input.setAttribute('aria-expanded', true);
      this.isOpen = true;
    }
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    
    this.classList.remove('run-top');

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
   
    this.isOpen = false;
    if(this.nextElementSibling && this.nextElementSibling.classList.contains('search-modal__close-button')) {
      this.nextElementSibling.dispatchEvent(new Event('click'));
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);